<script setup lang="ts">
import { useExplorerStore } from "@/modules/explorer/store";
import { computed, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { processedDocument } from "chartjs-graph";
import { formatNumber } from "chartjs-graph";
import useToast from "@/ui/composables/useToast";
import DownloadMenu from "@/modules/explorer/components/DownloadMenu.vue";
import ExportFileType = App.Enums.ExportFileType;
import { downloadData } from "@/modules/explorer/composables/DownloadData";

const dataIsVisible = defineModel<boolean>("dataIsVisible");
const dataType = defineModel<"graph-data" | "underlying-data">("dataType");
const downloadDialogIsVisible = ref(false);
const isLoadingData = ref(false);
const graphData = ref([]);
const rawData = ref([]);

const explorerStore = useExplorerStore();

const loadGraphData = async () => {
	isLoadingData.value = true;
	const response = await useApiClient("explorer").fetchRawDataForDocument(
		processedDocument(explorerStore.document)
	);

	isLoadingData.value = false;

	graphData.value = response.isOk() ? response.value : [];
};

const loadBaseData = async () => {
	isLoadingData.value = true;
	const response = await useApiClient("explorer").fetchBaseDataForDocument(
		processedDocument(explorerStore.document)
	);

	isLoadingData.value = false;

	rawData.value = response.isOk() ? response.value : [];
};

watch(dataType, (value) => {
	if (value && dataType.value === "underlying-data") {
		loadBaseData();
	}
	if (value && dataType.value === "graph-data") {
		loadGraphData();
	}
});

watch(dataIsVisible, async () => {
	if (dataType.value === "underlying-data") {
		await loadBaseData();
	}
	if (dataType.value === "graph-data") {
		await loadGraphData();
	}
});

const downloadUnderlyingData = async (exportFileType: ExportFileType) => {
	await downloadData(explorerStore.document, exportFileType, "underlying-data");
};

const downloadGraphData = async (exportFileType: ExportFileType) => {
	await downloadData(explorerStore.document, exportFileType, "graph-data");
};

const axisByLegend = computed(() => {
	const map = {};

	if (!graphData.value) return map;

	const vertical = explorerStore.document.vertical;
	const secondaryVertical = explorerStore.document.secondary_vertical ?? null;

	for (const graphObject of graphData.value) {
		if (graphObject.legend === vertical.name_clean) {
			map[graphObject.legend] = vertical.format;
		} else if (
			secondaryVertical &&
			graphObject.legend === secondaryVertical.name_clean
		) {
			map[graphObject.legend] = secondaryVertical.format;
		}
	}
	return map;
});

const copyData = async () => {
	let data;
	if (dataType.value === "underlying-data") {
		data = rawData.value;
	} else if (dataType.value === "graph-data") {
		data = graphData.value;
	}
	if (!data || !data.length) return;

	const headers = Object.keys(data[0]);
	const rows = data.map((row) => headers.map((h) => row[h]).join("\t"));

	const tsv = [headers.join("\t"), ...rows].join("\n");

	try {
		await navigator.clipboard.writeText(tsv);
		useToast().success("Copied to clipboard", {
			timeout: 2000,
		});
	} catch (err) {
		console.error("Failed to copy graph data:", err);
	}
};

const itemsPerPageOptions = [{ value: 50, title: "50" }];
</script>

<template>
	<v-dialog v-model="dataIsVisible" width="90vw">
		<v-card elevation="0" :loading="isLoadingData" :disabled="isLoadingData">
			<v-toolbar
				density="comfortable"
				style="border-radius: 0 !important"
				class="mb-0 pb-0"
				flat
			>
				<v-toolbar-title
					><i class="fa-duotone fa-binary fa-xl mr-3"> </i
					>{{
						dataType === "underlying-data" ? "Underlying Data" : "Graph Data"
					}}
				</v-toolbar-title>
				<v-spacer />

				<v-btn icon @click="dataIsVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-divider style="border-color: unset !important" />

			<template v-slot:loader="{ isActive }">
				<v-progress-linear
					:active="isActive"
					color="primary"
					height="4"
					indeterminate
				/>
			</template>

			<v-window v-model="dataType">
				<v-window-item value="underlying-data">
					<div
						v-if="explorerStore.document.vertical.type === 'mirrored'"
						class="pa-5"
					>
						Export is unavailable for a Mirrored-Graph Vertical. Please select
						the individual Verticals to export the data.
					</div>
					<template v-else>
						<perfect-scrollbar
							class="w-100"
							style="overflow-x: hidden; max-height: 46vh"
							:options="{ suppressScrollX: true }"
						>
							<v-alert
								v-if="explorerStore?.derivedDocument?.secondary_vertical"
								variant="tonal"
								color="warning"
								icon="mdi-alert"
								class="ma-3"
							>
								A secondary vertical is currently active; however, the data
								displayed here pertains solely to the primary vertical
								configuration.
							</v-alert>
							<v-data-table
								:items="rawData"
								:items-per-page="50"
								:items-per-page-options="itemsPerPageOptions"
							/>
							<v-alert
								v-if="rawData.length === 1000"
								variant="tonal"
								color="warning"
								icon="mdi-alert"
								class="ma-3"
							>
								The preview is limited to 1000 results. To view all the raw data
								used to generate this graph, please use the CSV export below.
							</v-alert>
						</perfect-scrollbar>
						<v-divider style="border-color: unset !important" />
						<v-card-actions class="pa-2">
							<v-spacer />
							<v-btn prepend-icon="fa-solid fa-clipboard" @click="copyData()"
								>Copy to clipboard</v-btn
							>
							<v-btn
								variant="flat"
								prepend-icon="mdi-download"
								@click="downloadDialogIsVisible = true"
							>
								Download underlying data
							</v-btn>
						</v-card-actions>
					</template>
				</v-window-item>

				<v-window-item value="graph-data">
					<perfect-scrollbar
						class="w-100"
						style="overflow-x: hidden; max-height: 46vh"
						:options="{ suppressScrollX: true }"
					>
						<v-data-table
							:items="graphData"
							:items-per-page="50"
							:items-per-page-options="itemsPerPageOptions"
						>
							<template v-slot:item.value="{ item }">
								{{ formatNumber(item.value, axisByLegend[item.legend]) }}
							</template>
						</v-data-table>
					</perfect-scrollbar>
					<v-divider style="border-color: unset !important" />
					<v-card-actions class="pa-3">
						<v-spacer></v-spacer>
						<v-btn prepend-icon="fa-solid fa-clipboard" @click="copyData"
							>Copy to clipboard</v-btn
						>
						<v-btn
							variant="flat"
							prepend-icon="mdi-download"
							@click="downloadDialogIsVisible = true"
							>Download graph data
						</v-btn>
					</v-card-actions>
				</v-window-item>
			</v-window>
		</v-card>
	</v-dialog>

	<download-menu
		v-if="downloadDialogIsVisible"
		v-model="downloadDialogIsVisible"
		:data-type="
			dataType === 'underlying-data' ? 'Underlying Data' : 'Graph Data'
		"
		@download="
			dataType === 'underlying-data'
				? downloadUnderlyingData($event)
				: downloadGraphData($event)
		"
	>
	</download-menu>
</template>

<style lang="scss">
.cm-wrap {
	height: 100%;
}

.cm-scroller {
	overflow: auto;
}
</style>
